// defaultValues baseline for the survey builder's RHF form (Pattern P8):
// per-field fallbacks (`|| ""`, Boolean casts), utcToLocalDatetimeInput for
// the dates, and the empty-question-row fallback for `questions`.

import { emptySurveyQuestionData } from "utils/consts";
import { utcToLocalDatetimeInput } from "./date-helpers";

/**
 * Shape a survey (plus its server-side questions) into the builder form's
 * value shape, field for field.
 *
 * `survey` is `{}` while SWR is still loading, and an omitted survey yields
 * the empty baseline; either way every field lands on a defined value.
 *
 * The empty-questions fallback is a fresh row per call
 * (`{ ...emptySurveyQuestionData }`), never the shared singleton, so callers
 * can never corrupt consts.js state by mutating form values. When the server
 * has questions, its array is passed through by reference; RHF deep-clones
 * defaultValues into its own state.
 *
 * @param {object} [survey] survey record from SWR (or `{}`)
 * @param {Array} [surveyQuestions] server-side questions, if loaded
 * @returns {object} builder form values
 */
export function shapeInitialSurveyValues(survey = {}, surveyQuestions) {
    return {
        name: survey.name || "",
        description: survey.description || "",
        start_text: survey.start_text || "",
        end_text: survey.end_text || "",
        survey_website: survey.survey_website || "",
        policy_url: survey.policy_url || "",
        use_comprehend: survey.use_comprehend || false,
        is_allow_anonymous: Boolean(survey.is_allow_anonymous),
        start_date: utcToLocalDatetimeInput(survey.start_date),
        end_date: utcToLocalDatetimeInput(survey.end_date),
        questions: surveyQuestions?.length
            ? surveyQuestions
            : [{ ...emptySurveyQuestionData }],
    };
}

/**
 * The `defaultValues` baseline for a never-loaded survey: what
 * shapeInitialSurveyValues produces with no survey data. Dates are ""
 * because utcToLocalDatetimeInput(null) returns "" (datetime-local inputs
 * take the empty string, not null), so the baseline matches what the form
 * sees the moment the shaped survey arrives.
 */
export const initialSurveyData = shapeInitialSurveyValues();

export default initialSurveyData;
